import {
  createAdminUser,
  deleteAdminUser,
  getMe,
  listAdminUsers,
  updateAdminUser,
  type CoworkAdminUser,
} from "@/cowork/api"
import { SettingsListV2 } from "@/components/settings-v2/parts/list"
import { SettingsRowV2 } from "@/components/settings-v2/parts/row"
import { useLanguage } from "@/context/language"
import { formatDateTime } from "@/utils/date"
import { createResource, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

type Draft = { full_name: string; status: "active" | "disabled" }

export function CoworkManageUsersPage() {
  const language = useLanguage()
  const [state, setState] = createStore({
    email: "",
    fullName: "",
    workspaceName: "",
    search: "",
    query: "",
    offset: 0,
    drafts: {} as Record<string, Draft | undefined>,
    busy: "",
    confirming: "",
    error: "",
    notice: "",
  })
  const [users, actions] = createResource(() => ({ search: state.query, offset: state.offset }), (params) =>
    listAdminUsers(params.search, params.offset),
  )
  const [me] = createResource(getMe)
  const draft = (user: CoworkAdminUser): Draft =>
    state.drafts[user.id] ?? { full_name: user.full_name ?? "", status: user.status === "active" ? "active" : "disabled" }
  const changed = (user: CoworkAdminUser) =>
    draft(user).full_name.trim() !== (user.full_name ?? "") || draft(user).status !== user.status

  const add = async (event: SubmitEvent) => {
    event.preventDefault()
    if (state.busy) return
    setState({ busy: "add", error: "", notice: "" })
    try {
      await createAdminUser({
        email: state.email.trim(),
        full_name: state.fullName.trim(),
        workspace_name: state.workspaceName.trim(),
      })
      setState({ email: "", fullName: "", workspaceName: "", notice: language.t("cowork.manageUsers.invited") })
      await actions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("busy", "")
    }
  }

  const save = async (user: CoworkAdminUser) => {
    if (state.busy || !draft(user).full_name.trim()) return
    setState({ busy: user.id, error: "", notice: "" })
    try {
      await updateAdminUser(user.id, { full_name: draft(user).full_name.trim(), status: draft(user).status })
      setState("drafts", user.id, undefined)
      setState("notice", language.t("cowork.manageUsers.saved"))
      await actions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("busy", "")
    }
  }

  const remove = async (user: CoworkAdminUser) => {
    if (state.busy) return
    setState({ busy: user.id, confirming: "", error: "", notice: "" })
    try {
      await deleteAdminUser(user.id)
      setState("drafts", user.id, undefined)
      setState("notice", language.t("cowork.manageUsers.deleted"))
      await actions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("busy", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1100px] space-y-7">
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.manageUsers.title")}</h1>
          <p class="mt-1 text-[13px] text-v2-text-text-muted">{language.t("cowork.manageUsers.description")}</p>
        </header>

        <SettingsListV2>
          <SettingsRowV2 title={language.t("cowork.manageUsers.add")} description={language.t("cowork.manageUsers.addDescription")}>
            <form class="flex flex-wrap items-center justify-end gap-2" onSubmit={add}>
              <input type="email" required aria-label={language.t("cowork.login.email")} placeholder={language.t("cowork.login.email")}
                class="h-9 w-48 rounded-md border border-v2-border-border-base px-2 text-[12px]"
                value={state.email} onInput={(event) => setState("email", event.currentTarget.value)} disabled={!!state.busy} />
              <input type="text" required aria-label={language.t("cowork.login.fullName")} placeholder={language.t("cowork.login.fullName")}
                class="h-9 w-40 rounded-md border border-v2-border-border-base px-2 text-[12px]"
                value={state.fullName} onInput={(event) => setState("fullName", event.currentTarget.value)} disabled={!!state.busy} />
              <input type="text" required aria-label={language.t("cowork.manageUsers.workspaceName")} placeholder={language.t("cowork.manageUsers.workspaceName")}
                class="h-9 w-40 rounded-md border border-v2-border-border-base px-2 text-[12px]"
                value={state.workspaceName} onInput={(event) => setState("workspaceName", event.currentTarget.value)} disabled={!!state.busy} />
              <button type="submit" disabled={!!state.busy} class="h-9 rounded-md bg-v2-background-bg-contrast px-3 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50">
                {language.t("cowork.manageUsers.invite")}
              </button>
            </form>
          </SettingsRowV2>
        </SettingsListV2>

        <Show when={state.notice || state.error}>
          <p role={state.error ? "alert" : "status"} class="text-[13px]">{state.error || state.notice}</p>
        </Show>

        <form class="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); setState({ query: state.search.trim(), offset: 0 }) }}>
          <input type="search" aria-label={language.t("cowork.manageUsers.search")} placeholder={language.t("cowork.manageUsers.search")}
            class="h-9 min-w-0 flex-1 rounded-md border border-v2-border-border-base px-2 text-[12px]"
            value={state.search} onInput={(event) => setState("search", event.currentTarget.value)} />
          <button type="submit" class="h-9 rounded-md border border-v2-border-border-base px-3 text-[12px]">{language.t("cowork.manageUsers.searchButton")}</button>
        </form>

        <SettingsListV2>
          <Show when={!users.error} fallback={<SettingsRowV2 title={language.t("cowork.manageUsers.error")} description={String(users.error)}><span /></SettingsRowV2>}>
            <Show when={!users.loading && (users()?.length ?? 0) > 0} fallback={<SettingsRowV2 title={users.loading ? language.t("cowork.manageUsers.loading") : language.t("cowork.manageUsers.empty")} description=""><span /></SettingsRowV2>}>
              <For each={users() ?? []}>
                {(user) => (
                  <SettingsRowV2 title={user.email ?? user.id} description={`${user.workspaces.join(", ")} · ${formatDateTime(user.created_at, { dateStyle: "short" })}`}>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                      <span class="text-[11px] text-v2-text-text-muted">{language.t(user.status === "deleted" ? "cowork.manageUsers.status.deleted" : user.status === "disabled" ? "cowork.manageUsers.status.disabled" : !user.has_password ? "cowork.manageUsers.status.pending" : "cowork.manageUsers.status.active")}</span>
                      <input type="text" aria-label={language.t("cowork.login.fullName")} class="h-8 w-36 rounded-md border border-v2-border-border-base px-2 text-[12px]"
                        value={draft(user).full_name} disabled={!!state.busy || user.status === "deleted"}
                        onInput={(event) => setState("drafts", user.id, { ...draft(user), full_name: event.currentTarget.value })} />
                      <select aria-label={language.t("cowork.manageUsers.accountStatus")} class="h-8 rounded-md border border-v2-border-border-base px-2 text-[12px]"
                        value={draft(user).status} disabled={!!state.busy || user.status === "deleted" || user.id === me()?.user_id}
                        onChange={(event) => setState("drafts", user.id, { ...draft(user), status: event.currentTarget.value as Draft["status"] })}>
                        <option value="active">{language.t("cowork.manageUsers.status.active")}</option>
                        <option value="disabled">{language.t("cowork.manageUsers.status.disabled")}</option>
                      </select>
                      <Show when={user.status !== "deleted"}>
                        <button type="button" disabled={!!state.busy || !changed(user) || !draft(user).full_name.trim()}
                          class="h-8 rounded-md bg-v2-background-bg-contrast px-3 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                          onClick={() => void save(user)}>{language.t("cowork.adminWorkspaces.save")}</button>
                        <Show when={user.id !== me()?.user_id}>
                          <Show when={state.confirming === user.id} fallback={
                            <button type="button" disabled={!!state.busy} class="h-8 rounded-md border border-v2-border-border-base px-3 text-[11px]"
                              onClick={() => setState("confirming", user.id)}>{language.t("cowork.manageUsers.delete")}</button>
                          }>
                            <button type="button" disabled={!!state.busy} class="h-8 rounded-md border border-v2-border-border-base px-3 text-[11px] text-v2-state-fg-danger"
                              onClick={() => void remove(user)}>{language.t("cowork.manageUsers.confirmDelete")}</button>
                            <button type="button" class="h-8 px-2 text-[11px]" onClick={() => setState("confirming", "")}>{language.t("cowork.manageUsers.cancel")}</button>
                          </Show>
                        </Show>
                      </Show>
                    </div>
                  </SettingsRowV2>
                )}
              </For>
            </Show>
          </Show>
        </SettingsListV2>
        <div class="flex justify-end gap-3">
          <button type="button" disabled={state.offset === 0} onClick={() => setState("offset", Math.max(0, state.offset - 50))}>{language.t("cowork.manageUsers.previous")}</button>
          <button type="button" disabled={(users()?.length ?? 0) < 50} onClick={() => setState("offset", state.offset + 50)}>{language.t("cowork.manageUsers.next")}</button>
        </div>
      </div>
    </div>
  )
}
